import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

interface ReturnReq {
  id: string;
  reference: string;
  productTitle: string;
  reason: string;
  notes?: string;
  state: 'OPEN' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED' | 'COMPLETE';
  createdAt: string;
  resolvedAt?: string;
}

const stateMap: Record<ReturnReq['state'], string> = {
  OPEN:      'pill-warn',
  IN_REVIEW: 'pill-clay',
  APPROVED:  'pill-ok',
  REJECTED:  'pill-bad',
  COMPLETE:  'pill-ok',
};

export default function Returns({ embedded = false }: { embedded?: boolean }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<ReturnReq[]>({
    queryKey: ['returns'],
    queryFn: async () => (await api.get('/me/returns')).data,
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/returns/${id}`)).data,
    onSuccess: () => { toast.success('Return cancelled.'); qc.invalidateQueries({ queryKey: ['returns'] }); },
  });

  return (
    <div>
      {!embedded && (
        <>
          <div className="stamp text-gold-dark">Returns & disputes</div>
          <h1 className="display text-4xl mt-2">Returns</h1>
        </>
      )}
      <p className="text-sm text-mute max-w-[60ch]">
        Open within 48 hours of collection. Souqli mediates within 3 working days; funds remain held until resolution.
      </p>

      <div className="mt-6 space-y-4">
        {rows.map((r) => (
          <div key={r.id} className="card p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="stamp text-mute mono">{r.reference}</div>
                <div className="display text-xl mt-1">{r.productTitle}</div>
                <div className="text-sm text-ink-700 mt-2">
                  <span className="stamp text-mute">Reason -</span> {r.reason}
                </div>
                {r.notes && <p className="text-sm text-mute mt-1.5 italic">"{r.notes}"</p>}
                <div className="stamp text-mute mt-3">Opened {new Date(r.createdAt).toLocaleDateString()}{r.resolvedAt ? ` · Resolved ${new Date(r.resolvedAt).toLocaleDateString()}` : ''}</div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`pill ${stateMap[r.state]}`}>{r.state.replace('_',' ').toLowerCase()}</span>
                {r.state === 'OPEN' && (
                  <button onClick={() => cancel.mutate(r.id)} className="text-xs text-bad hover:underline">Cancel return</button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!rows.length && (
          <div className="card p-10 text-center">
            <div className="text-3xl mb-2">🌿</div>
            <div className="display text-2xl text-mute">No returns open</div>
            <p className="text-sm text-mute mt-2">Hopefully it stays that way.</p>
          </div>
        )}
      </div>
    </div>
  );
}
